import { useCallback, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMsal } from "@azure/msal-react";
import { getAuthenticationMethod } from "../config/runtimeConfig";

/**
 * Returns the current user's raw JWT access token as a string.
 *
 * useAuthFetch handles ordinary authenticated requests. This hook exposes the
 * raw token for transports that manage authentication themselves without
 * duplicating auth-provider logic.
 *
 * HTTP and SSE consumers must put this value in an Authorization header and
 * must never put it in a URL. Native WebSocket cannot set request headers; the
 * remaining WebSocket caller is a separate transport with its own backend
 * authentication path.
 *
 * SSE usage:
 *   const getAuthToken = useAuthToken();
 *   const token = await getAuthToken();
 *   const response = await fetch(`/api/.../stream`, {
 *     headers: { Authorization: `Bearer ${token}` },
 *   });
 */
const useAuthToken = (): (() => Promise<string>) => {
  const { getAccessTokenSilently } = useAuth0();
  const { instance: msalInstance, accounts: msalAccounts } = useMsal();

  const authMethod = useMemo(() => getAuthenticationMethod(), []);

  const getToken = useCallback(async (): Promise<string> => {
    if (authMethod === "AUTH0") {
      return await getAccessTokenSilently();
    }
    if (authMethod === "ENTRAID") {
      const request = {
        scopes: [
          "api://0501f640-94a0-4fd9-a7ae-0e8fa5938130/access_as_user",
          "email",
          "openid",
          "profile",
        ],
        account:
          msalAccounts[0] ?? msalInstance.getActiveAccount() ?? undefined,
      };
      await msalInstance.initialize();
      const response = await msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    }
    // UNSAFE mode -- no token needed
    return "";
  }, [authMethod, getAccessTokenSilently, msalInstance, msalAccounts]);

  return getToken;
};

export default useAuthToken;
